import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../../../api/client";
import useAuth from "../../../../hooks/useAuth";

const STAGE_TYPES = [
  { value: "production", label: "Production" },
  { value: "raw_material", label: "Raw Material Order" },
  { value: "quality_check", label: "Quality Check" },
  { value: "dispatch", label: "Dispatch" },
  { value: "custom", label: "Custom" },
];

const TYPE_COLOR = {
  production: "#7c3aed",
  raw_material: "#1d4ed8",
  quality_check: "#d97706",
  dispatch: "#15803d",
  custom: "#64748b",
};

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

function newStage(order) {
  return {
    _key: Math.random().toString(36).slice(2),
    name: "",
    type: "production",
    order,
    assigneeId: "",
    whatsappNotify: true,
    triggerStage: "",
  };
}

export default function WorkflowBuilder() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [name, setName] = useState("Untitled Workflow");
  const [status, setStatus] = useState("draft");
  const [version, setVersion] = useState(1);
  const [stages, setStages] = useState([newStage(0)]);
  const [users, setUsers] = useState([]);
  const [unsaved, setUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const loadingRef = useRef(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const markUnsaved = useCallback(() => {
    if (loadingRef.current) return;
    setUnsaved(true);
  }, []);

  // Load users for assignee dropdown
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.users || [])).catch(() => {});
  }, []);

  // Load existing workflow
  useEffect(() => {
    if (isNew) {
      setUnsaved(false);
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    api
      .get(`/manufacturing/workflows/${id}`)
      .then((r) => {
        const w = r.data.data;
        setName(w.name);
        setStatus(w.status);
        setVersion(w.version);
        setStages(
          (w.stages || []).map((s) => ({
            _key: s.id,
            id: s.id,
            name: s.name,
            type: s.type,
            order: s.order,
            assigneeId: s.assigneeId || "",
            whatsappNotify: s.whatsappNotify,
            triggerStage: s.triggerStage != null ? String(s.triggerStage) : "",
          })),
        );
        setUnsaved(false);
      })
      .catch(() => showToast("Failed to load workflow", "error"))
      .finally(() => {
        loadingRef.current = false;
        setLoading(false);
      });
  }, [id, isNew]);

  const buildPayload = () => ({
    name: name.trim(),
    status,
    stages: stages.map((s, i) => ({
      name: s.name,
      type: s.type,
      order: i,
      assigneeId: s.assigneeId || null,
      whatsappNotify: s.whatsappNotify,
      triggerStage: s.triggerStage !== "" ? Number(s.triggerStage) : null,
    })),
  });

  const handleSave = async () => {
    if (!name.trim()) { showToast("Workflow name is required", "error"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const resp = await api.post("/manufacturing/workflows", buildPayload());
        setUnsaved(false);
        showToast("Workflow created");
        navigate(`/manufacturing/settings/workflows/${resp.data.data.id}`, { replace: true });
      } else {
        const resp = await api.put(`/manufacturing/workflows/${id}`, buildPayload());
        setVersion(resp.data.data.version);
        setUnsaved(false);
        showToast("Workflow saved");
      }
    } catch (err) {
      showToast(err.response?.data?.error || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const addStage = () => {
    setStages((prev) => [...prev, newStage(prev.length)]);
    markUnsaved();
  };

  const updateStage = (idx, field, value) => {
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
    markUnsaved();
  };

  const removeStage = (idx) => {
    setStages((prev) => prev.filter((_, i) => i !== idx));
    markUnsaved();
  };

  // Drag-and-drop reorder
  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    setStages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(null);
    setDragOverIdx(null);
    markUnsaved();
  };

  if (loading) {
    return <div style={{ padding: 24, color: "#64748b" }}>Loading workflow...</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: "#f5f8fc" }}>
      {/* Top bar */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #e4ebf5",
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <Link className="back-link" to="/manufacturing/settings/workflows">
          ← Back to Workflows
        </Link>

        <input
          value={name}
          onChange={(e) => { setName(e.target.value); markUnsaved(); }}
          style={{
            fontSize: 16,
            fontWeight: 700,
            border: "1px solid #d6dfed",
            borderRadius: 8,
            padding: "6px 12px",
            color: "#0f2d5c",
            minWidth: 200,
          }}
        />

        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); markUnsaved(); }}
          style={{
            border: "1px solid #d6dfed",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <span style={{ fontSize: 12, color: "#94a3b8", background: "#f1f5f9", borderRadius: 999, padding: "3px 10px" }}>
          v{version}
        </span>

        {unsaved && (
          <span style={{ fontSize: 12, fontWeight: 600, color: "#d97706", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 999, padding: "3px 10px" }}>
            Unsaved changes
          </span>
        )}

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button type="button" className="btn-secondary" onClick={addStage} style={{ fontSize: 13 }}>
            + Add Stage
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
            style={{ fontSize: 13 }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            background: toast.type === "error" ? "#fee2e2" : "#dcfce7",
            color: toast.type === "error" ? "#991b1b" : "#14532d",
            border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
            borderRadius: 10,
            padding: "12px 20px",
            fontWeight: 600,
            fontSize: 14,
            zIndex: 500,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          }}
        >
          {toast.msg}
        </div>
      )}

      {/* Stage list */}
      <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
        {stages.length === 0 && (
          <div
            style={{
              background: "#fff",
              border: "2px dashed #d6dfed",
              borderRadius: 12,
              padding: 32,
              textAlign: "center",
              color: "#94a3b8",
            }}
          >
            No stages yet.{" "}
            <button type="button" style={{ color: "#0f62fe", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }} onClick={addStage}>
              Add your first stage
            </button>
          </div>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          {stages.map((stage, idx) => {
            const typeColor = TYPE_COLOR[stage.type] || TYPE_COLOR.custom;
            const isDragOver = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;
            return (
              <div
                key={stage._key}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDrop={() => onDrop(idx)}
                onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                style={{
                  background: "#fff",
                  border: isDragOver ? `2px solid ${typeColor}` : "1px solid #e4ebf5",
                  borderRadius: 12,
                  overflow: "hidden",
                  opacity: dragIdx === idx ? 0.5 : 1,
                  transition: "border 0.15s",
                }}
              >
                {/* Card header strip */}
                <div
                  style={{
                    background: typeColor,
                    padding: "8px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  <span style={{ cursor: "grab", fontSize: 16, opacity: 0.8 }}>⣿</span>
                  <span>Stage {idx + 1}</span>
                  <span style={{ opacity: 0.8, fontWeight: 500 }}>
                    {STAGE_TYPES.find((t) => t.value === stage.type)?.label || stage.type}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeStage(idx)}
                    style={{
                      marginLeft: "auto",
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      color: "#fff",
                      borderRadius: 6,
                      padding: "2px 8px",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Remove
                  </button>
                </div>

                {/* Card body */}
                <div style={{ padding: 16, display: "grid", gap: 12 }}>
                  {/* Row 1: name + type */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Stage Name</label>
                      <input
                        className="um-input"
                        placeholder="e.g. Cutting"
                        value={stage.name}
                        onChange={(e) => updateStage(idx, "name", e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Type</label>
                      <select
                        className="um-input"
                        value={stage.type}
                        onChange={(e) => updateStage(idx, "type", e.target.value)}
                      >
                        {STAGE_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 2: assignee + units per day */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
                    <div>
                      <label style={labelStyle}>Assignee</label>
                      <select
                        className="um-input"
                        value={stage.assigneeId}
                        onChange={(e) => updateStage(idx, "assigneeId", e.target.value)}
                      >
                        <option value="">— Unassigned —</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 4 }}>
                      <label style={{ ...labelStyle, marginBottom: 0 }}>WhatsApp Notify</label>
                      <button
                        type="button"
                        onClick={() => updateStage(idx, "whatsappNotify", !stage.whatsappNotify)}
                        style={{
                          width: 44,
                          height: 24,
                          borderRadius: 999,
                          border: "none",
                          background: stage.whatsappNotify ? "#15803d" : "#cbd5e1",
                          cursor: "pointer",
                          position: "relative",
                          transition: "background 0.2s",
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: 3,
                            left: stage.whatsappNotify ? 22 : 3,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            background: "#fff",
                            transition: "left 0.2s",
                          }}
                        />
                      </button>
                      <span style={{ fontSize: 12, color: stage.whatsappNotify ? "#15803d" : "#94a3b8", fontWeight: 600 }}>
                        {stage.whatsappNotify ? "On" : "Off"}
                      </span>
                    </div>
                  </div>

                  {/* Raw material trigger field */}
                  {stage.type === "raw_material" && (
                    <div style={{ maxWidth: "50%" }}>
                      <label style={labelStyle}>Trigger Alert at Stage #</label>
                      <input
                        className="um-input"
                        type="number"
                        placeholder="e.g. 3"
                        value={stage.triggerStage}
                        onChange={(e) => updateStage(idx, "triggerStage", e.target.value)}
                      />
                      <p className="um-note" style={{ margin: "4px 0 0" }}>
                        Stage number whose start triggers the next batch order alert
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#4f6280",
  marginBottom: 4,
};
